"use client";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/AccountReadPending/AccountReadPendingContent";

const I18n = I18nUtil.createScoped(Translations);

export interface AccountReadPendingContentProps {
  /** 账户侧别：BANKING | INVEST */
  side?: "BANKING" | "INVEST";
  /** 是否展示开启通知主按钮（默认 true 对应设计稿默认双 CTA；仅在已授权或冷却拦截时传 false 隐藏） */
  showTurnOnNotifications?: boolean;
  /** 点击「Turn on notifications」回调 */
  onTurnOnNotifications?: () => void;
  /** 点击「Got it」关闭回调 */
  onGotIt: () => void;
}

/**
 * F9 · Account read pending Popup
 *
 * 规格源：
 * - 移动端（Popup Gen `107353:220749`）：
 *   - 容器：300×380、圆角 12、底色 `#F9F9F3`（bg-background_solid_neutral_primary）
 *   - 内容区：252 宽（左右 padding 24）
 *   - 标题 2 行（56px）、正文 3 行（60px）
 * - PC 端（Popup Gen `107476:53180`）：
 *   - 容器：400×332、圆角 12、底色 `#F9F9F3`（bg-background_solid_neutral_primary）
 *   - 内容区：352 宽（左右 padding 24）
 *   - 标题 1 行（28px）、正文 2 行（40px）
 * - Icon 区（两端一致）：72×72 容器内，Spinner 环绕居中 40×40 wallet 图标
 *   - Track：#036263 @ 20% opacity (stroke-background_brand_overlay20)
 *   - Arc：#036263 (stroke-background_brand_overlay100) 旋转动效
 * - 标题：headline_regular_s（20/28 ls-0.8）+ text-content_primary
 * - 正文：body_m（14/20）+ text-content_secondary
 * - Actions：
 *   - 双 CTA：主按钮 44 高（button md_button primary_button button_m）+ Got it 44 高（button md_button text_button button_m）
 *   - 单 CTA：已授权或冷却中仅保留 Got it，卡片其余分区不动
 */
export const AccountReadPendingContent: React.FC<
  AccountReadPendingContentProps
> = ({ showTurnOnNotifications = true, onTurnOnNotifications, onGotIt }) => {
  // EDG-529 裁定 2026-09-10（对齐 Figma 107353:220749）：弹窗为纯静态设计
  // （spinner+文案+双 CTA）——同步是后台的事，完成走通知推送；不轮询
  // sync-status、不展示账户计数/进度。

  return (
    <div className="relative flex h-[380px] w-[300px] flex-col items-center justify-between rounded-[12px] bg-background_solid_neutral_primary px-6 pb-4 pt-8 text-content_primary shadow-2xl md:h-[332px] md:w-[400px]">
      {/* Body Section (Icon + Copy) */}
      <div className="flex w-full flex-1 flex-col items-center">
        {/* Icon: 72×72 Spinner 环绕 40×40 wallet */}
        <div className="relative flex size-[72px] shrink-0 items-center justify-center">
          <svg
            className="absolute inset-0 size-full"
            viewBox="0 0 72 72"
            fill="none"
            aria-hidden="true"
          >
            {/* Spinner Track (20% opacity) */}
            <circle
              cx="36"
              cy="36"
              r="34"
              strokeWidth="3"
              className="stroke-background_brand_overlay20"
            />
            {/* Spinner Arc (spinning) */}
            <circle
              cx="36"
              cy="36"
              r="34"
              strokeWidth="3"
              strokeDasharray="60 160"
              strokeLinecap="round"
              className="animate-spin origin-center stroke-background_brand_overlay100"
            />
          </svg>
          <Icon
            name="wallet"
            size={40}
            className="relative z-1 shrink-0 text-content_primary"
          />
        </div>

        {/* Copy (Title + Desc) */}
        <div className="mt-6 flex w-full flex-col items-center gap-2">
          <h2 className="headline_regular_s w-full text-center text-content_primary tracking-[-0.8px]">
            {I18n.t("I'm reading your accounts now")}
          </h2>
          <p className="body_m w-full text-center text-content_secondary">
            {I18n.t(
              "Your first read is ready in a few minutes. Turn on notifications and I'll send it the moment it's done.",
            )}
          </p>
        </div>
      </div>

      {/* Actions (Mobile 252×88 / PC 352×88) */}
      <div className="mt-6 flex w-full flex-col gap-0 shrink-0">
        {showTurnOnNotifications && (
          <button
            type="button"
            onClick={onTurnOnNotifications}
            className="button md_button primary_button button_m flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-background_brand_overlay100 text-content_inverse_primary hover:opacity-90 active:scale-[0.99] transition-all cursor-pointer"
            data-openreplay-label="account_read_pending_turn_on_notifications"
          >
            <Icon
              name="bell_empty"
              size={20}
              className="shrink-0 text-content_inverse_primary"
            />
            <span>{I18n.t("Turn on notifications")}</span>
          </button>
        )}
        <button
          type="button"
          onClick={onGotIt}
          className="button md_button text_button button_m flex h-11 w-full items-center justify-center rounded-lg text-content_primary hover:opacity-80 active:scale-[0.99] transition-all cursor-pointer"
          data-openreplay-label="account_read_pending_got_it"
        >
          <span>{I18n.t("Got it")}</span>
        </button>
      </div>
    </div>
  );
};
